/**
 * Email page — one email from the queue: where it is in processing, what it
 * said, what was attached, and the requests created from it.
 *
 * The header carries the subject, the email's status and a four-step track
 * (Received → Queued → Processing → Processed/Failed), so where it is reads
 * at a glance. While the email is still queued or processing, the page
 * refreshes itself quietly and the track moves on by itself.
 *
 * Same endpoints as before: GET /emails/:id for the email (documents
 * included) and the requests list filtered by email_id.
 */
import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { emailsApi, requestsApi } from '../services/api'
import { formatDateTime, formatDate, formatTime, formatRecent, parseUTC } from '../utils/dateUtils'
import { describeFile, formatFileSize } from '../utils/fileTypes'
import { useAuth } from '../context/AuthContext'
import { isReadOnlyUser } from '../utils/readerAccess'
import UserAvatar from '../components/UserAvatar'
import { FileGlyph, DocumentStatusChip } from '../components/documents/FileGlyph'
import { trackSpotlight } from '../components/collection/cardInteractions'
import { CopyButton, DetailRow, PanelCard } from '../components/detail/DetailPrimitives'
import { REQUEST_STATUS, TONE_CHIP, TONE_DOT } from '../components/detail/requestStatus'
import EmailStatusBadge from '../components/emails/EmailStatusBadge'
import { LIVE_INTERVAL_MS, avatarTone, getStatusMeta, initialsFor, parseSender } from '../components/emails/emailStatus'
import {
  ArrowLeft,
  ArrowRight,
  Mail,
  Paperclip,
  FileText,
  FileStack,
  Calendar,
  AlertCircle,
  RefreshCw,
  Eye,
  Edit3,
  Link2,
  Hash,
  Clock,
  CheckCircle2,
  Check,
  X,
  Loader2,
  Inbox,
  History,
  Sparkles,
  ChevronDown,
  AtSign,
} from 'lucide-react'

// Statuses the email can still move on from; the page watches these.
const IN_FLIGHT = new Set(['pending', 'processing'])

function EmailDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [email, setEmail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [requests, setRequests] = useState([])
  const [requestsLoading, setRequestsLoading] = useState(false)
  const [requestsLoaded, setRequestsLoaded] = useState(false)
  const [requestsError, setRequestsError] = useState(false)
  const [statusChanged, setStatusChanged] = useState(false)
  const emailSeq = useRef(0)
  const requestsSeq = useRef(0)
  const lastStatus = useRef(null)
  const requestsRef = useRef(null)

  /** `silent` is the live refresh: no loading state, and a failure keeps what's on screen. */
  const fetchEmail = useCallback(async ({ silent = false } = {}) => {
    const seq = ++emailSeq.current
    try {
      if (!silent) setLoading(true)
      const res = await emailsApi.get(id)
      if (seq !== emailSeq.current) return
      const next = res.data
      setStatusChanged(lastStatus.current != null && lastStatus.current !== next?.status)
      lastStatus.current = next?.status ?? null
      setEmail(next)
      setError(null)
    } catch (err) {
      if (seq !== emailSeq.current) return
      console.error('Error fetching email:', err)
      if (silent) return
      const code = err?.response?.status
      setError(
        code === 404
          ? { title: 'Email not found', message: 'It may have been deleted, or the link is out of date.' }
          : code === 403
            ? { title: 'You don’t have access to this email', message: 'It belongs to a different organisation.' }
            : { title: 'Failed to load email', message: 'Check your connection and try again.', retry: true },
      )
    } finally {
      if (seq === emailSeq.current) setLoading(false)
    }
  }, [id])

  const fetchRequests = useCallback(async ({ silent = false } = {}) => {
    const seq = ++requestsSeq.current
    try {
      if (!silent) setRequestsLoading(true)
      const res = await requestsApi.list({ email_id: id, per_page: 50 })
      if (seq !== requestsSeq.current) return
      setRequests(res.data.requests || [])
      setRequestsError(false)
    } catch (err) {
      if (seq !== requestsSeq.current) return
      console.error('Error fetching requests for email:', err)
      if (!silent) setRequestsError(true)
    } finally {
      if (seq === requestsSeq.current) {
        setRequestsLoading(false)
        setRequestsLoaded(true)
      }
    }
  }, [id])

  useEffect(() => {
    lastStatus.current = null
    setEmail(null)
    setRequests([])
    setRequestsLoaded(false)
    fetchEmail()
    fetchRequests()
  }, [fetchEmail, fetchRequests])

  // Quiet refresh while the email is still on its way through.
  const live = Boolean(email && IN_FLIGHT.has(email.status))
  useEffect(() => {
    if (!live) return undefined
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      fetchEmail({ silent: true })
      fetchRequests({ silent: true })
    }, LIVE_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [live, fetchEmail, fetchRequests])

  const refreshAll = () => {
    fetchEmail()
    fetchRequests()
  }

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/emails'))

  if (loading && !email) {
    return <DetailSkeleton />
  }

  if (error) {
    return (
      <div className="mx-auto mt-10 max-w-md text-center animate-rise-in">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 ring-1 ring-red-100 dark:bg-red-900/20 dark:ring-red-900/40">
          <AlertCircle className="h-7 w-7 text-red-500 dark:text-red-400" />
        </div>
        <h1 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">{error.title}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          {error.retry && (
            <button
              type="button"
              onClick={() => fetchEmail()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-navy-800 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-navy-900 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Try again
            </button>
          )}
        </div>
      </div>
    )
  }

  if (!email) {
    return null
  }

  const meta = getStatusMeta(email.status)
  const documents = email.documents || []
  const refreshing = loading || requestsLoading

  return (
    <div className="space-y-5">
      {/* Header */}
      <header
        className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] animate-rise-in dark:border-gray-800 dark:bg-gray-900"
        style={{ '--rise-delay': '40ms' }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-70 transition-[background] duration-700 dark:opacity-50"
          style={{ background: `radial-gradient(260px circle at 3.5rem 3.5rem, rgb(${meta.glow} / 0.09), transparent 70%)` }}
        />
        <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-5">
            <EnvelopeMark status={email.status} />
            <div className="min-w-0">
              <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 [overflow-wrap:anywhere] dark:text-gray-50 sm:text-2xl">
                {email.subject || 'No subject'}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
                <EmailStatusBadge status={email.status} changed={statusChanged} />
                <SenderChip sender={email.sender} />
                <span className="tabular-nums">Email #{email.id}</span>
                <span className="inline-flex items-center gap-1.5">
                  <Paperclip className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                  {documents.length} attachment{documents.length !== 1 ? 's' : ''}
                </span>
                {(email.received_at || email.created_at) && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    <time dateTime={email.received_at || email.created_at} title={formatDateTime(email.received_at || email.created_at)}>
                      Received {formatRecent(email.received_at || email.created_at)}
                    </time>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:flex-shrink-0">
            <CopyButton
              value={() => window.location.href}
              label="Copy link"
              copiedLabel="Link copied"
              icon={Link2}
              className="h-9 w-9"
            />
            <button
              type="button"
              onClick={refreshAll}
              disabled={refreshing}
              title="Refresh the email and its requests"
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white/80 px-3.5 text-sm font-medium text-gray-700 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 hover:bg-white enabled:hover:-translate-y-0.5 enabled:hover:shadow-sm enabled:active:translate-y-0 enabled:active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-200 dark:hover:border-gray-600 dark:hover:bg-gray-800"
            >
              <RefreshCw className={`h-4 w-4 text-gray-500 dark:text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <PrimaryRequestAction requests={requests} onShowAll={() => requestsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
          </div>
        </div>

        <div className="relative grid gap-5 border-t border-gray-100 bg-gray-50/60 px-5 py-5 dark:border-gray-800 dark:bg-white/[0.015] sm:px-6 lg:grid-cols-[minmax(0,1fr)_17.5rem] lg:items-center lg:gap-8">
          <ProgressTrack email={email} />
          <OutcomeSummary email={email} live={live} />
        </div>
      </header>

      {/* Message + attachments, with the details beside them */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_21rem] xl:grid-cols-[minmax(0,1fr)_23rem]">
        <div className="min-w-0 space-y-5">
          <MessageCard email={email} />
          <AttachmentsCard documents={documents} />
        </div>

        <aside className="space-y-5 lg:sticky lg:top-[5.5rem]">
          <DetailsCard email={email} />
          {email.body_analysis && <BodyAnalysisCard analysis={email.body_analysis} />}
        </aside>
      </div>

      <RequestsSection
        sectionRef={requestsRef}
        requests={requests}
        loading={requestsLoading}
        loaded={requestsLoaded}
        failed={requestsError}
        onRefresh={() => fetchRequests()}
      />
    </div>
  )
}

// ── Header ───────────────────────────────────────────────────────────────────

// Icon colour per status on the neutral envelope tile.
const STATUS_INK = {
  pending: 'text-amber-500 dark:text-amber-300',
  processing: 'text-sky-500 dark:text-sky-300',
  processed: 'text-emerald-600 dark:text-emerald-400',
  failed: 'text-rose-500 dark:text-rose-400',
}

/** The email's mark: a neutral envelope tile, its status in the icon colour and a corner dot. */
function EnvelopeMark({ status }) {
  const meta = getStatusMeta(status)
  return (
    <div className="group relative hidden flex-shrink-0 sm:block" aria-hidden="true">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_8px_20px_-12px_rgba(16,24,40,0.25)] ring-1 ring-gray-200 transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:-rotate-6 group-hover:scale-105 dark:bg-gray-800 dark:shadow-none dark:ring-gray-700">
        <Mail className={`h-6 w-6 transition-colors duration-500 ${STATUS_INK[status] || 'text-navy-600 dark:text-navy-300'}`} />
      </div>
      <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5">
        {IN_FLIGHT.has(status) && <span className={`absolute inline-flex h-full w-full rounded-full opacity-70 motion-safe:animate-ping ${meta.bar}`} />}
        <span className={`relative inline-flex h-3.5 w-3.5 rounded-full ring-[3px] ring-white transition-colors duration-500 dark:ring-gray-900 ${meta.bar}`} />
      </span>
    </div>
  )
}

function requestHref(request, isReader) {
  const mode = (request.status === 'completed' || request.status === 'cancelled' || isReader) ? 'view' : 'edit'
  return `/requests/${request.id}?mode=${mode}`
}

/** One request: open it. Several: jump to the list. None: nothing to offer. */
function PrimaryRequestAction({ requests, onShowAll }) {
  const { user } = useAuth()
  const isReader = isReadOnlyUser(user)
  if (!requests.length) return null
  const className = 'group inline-flex h-9 items-center gap-2 rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white shadow-sm ring-1 ring-navy-900/20 transition-all duration-200 animate-panel-in hover:-translate-y-0.5 hover:bg-navy-900 hover:shadow-lg active:translate-y-0 active:scale-[0.98]'
  if (requests.length === 1) {
    return (
      <Link to={requestHref(requests[0], isReader)} className={className}>
        <FileStack className="h-4 w-4" />
        Open request
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
      </Link>
    )
  }
  return (
    <button type="button" onClick={onShowAll} className={className}>
      <FileStack className="h-4 w-4" />
      View {requests.length} requests
      <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
    </button>
  )
}

/** Who sent it: initials in the sender's colour, then their name. */
function SenderChip({ sender: raw }) {
  const sender = parseSender(raw)
  return (
    <span className="inline-flex min-w-0 max-w-[16rem] items-center gap-1.5" title={raw || undefined}>
      <span aria-hidden="true" className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-semibold ${avatarTone(sender.address || sender.name)}`}>
        {initialsFor(sender)}
      </span>
      <span className="truncate font-medium text-gray-700 dark:text-gray-300">{sender.name}</span>
    </span>
  )
}

/** "32 s", "4 min 10 s", "3 h 5 min", "2 days". Null when there's nothing sensible to say. */
function formatDuration(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null
  const sec = Math.round(ms / 1000)
  if (sec < 1) return 'under a second'
  if (sec < 60) return `${sec} s`
  const min = Math.floor(sec / 60)
  if (min < 60) return sec % 60 ? `${min} min ${sec % 60} s` : `${min} min`
  const hrs = Math.floor(min / 60)
  if (hrs < 24) return min % 60 ? `${hrs} h ${min % 60} min` : `${hrs} h`
  const days = Math.floor(hrs / 24)
  return `${days} day${days !== 1 ? 's' : ''}`
}

/** The current time, re-read every `intervalMs` while set; frozen otherwise. */
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!intervalMs) return undefined
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(timer)
  }, [intervalMs])
  return now
}

const OUTCOME = {
  processed: { icon: Check, title: 'Processed successfully', ring: 'bg-emerald-500 text-white shadow-[0_0_0_4px_rgb(16_185_129/0.14)]' },
  failed: { icon: X, title: 'Processing failed', ring: 'bg-rose-500 text-white shadow-[0_0_0_4px_rgb(244_63_94/0.14)]' },
  processing: { icon: Loader2, spin: true, title: 'Extracting data', ring: 'bg-sky-50 text-sky-600 ring-1 ring-inset ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/30' },
  pending: { icon: Clock, title: 'Waiting in the queue', ring: 'bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/30' },
}

/**
 * The upshot, beside the track: how it ended and how long it took, or, while
 * it's still going, how long it has been going, counting up live.
 */
function OutcomeSummary({ email, live }) {
  const now = useNow(live ? 1000 : null)
  const outcome = OUTCOME[email.status] || { icon: Mail, title: getStatusMeta(email.status).label, ring: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' }
  const Icon = outcome.icon
  const queuedAt = parseUTC(email.created_at)?.getTime()
  const finishedAt = parseUTC(email.updated_at)?.getTime()
  const took = queuedAt && finishedAt ? formatDuration(finishedAt - queuedAt) : null
  const running = queuedAt ? formatDuration(now - queuedAt) : null

  let detail = null
  if (email.status === 'processed') detail = took ? <>Took <Strong>{took}</Strong> from queue to finish</> : 'Ready to review below'
  else if (email.status === 'failed') detail = took ? <>Stopped after <Strong>{took}</Strong></> : 'Something went wrong while processing'
  else if (email.status === 'processing') detail = running && <>Running for <Strong>{running}</Strong></>
  else if (email.status === 'pending') detail = running && <>Waiting for <Strong>{running}</Strong></>

  return (
    <div
      key={email.status}
      className="flex items-center gap-3 rounded-xl bg-white p-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.05)] ring-1 ring-gray-200/80 animate-panel-in dark:bg-gray-900 dark:shadow-none dark:ring-gray-800"
    >
      <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${outcome.ring}`}>
        <Icon className={`h-4 w-4 ${outcome.spin ? 'motion-safe:animate-spin' : ''}`} strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{outcome.title}</p>
        {detail && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{detail}</p>}
        {live && (
          <p
            className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-300"
            title={`Updating every ${LIVE_INTERVAL_MS / 1000} seconds until processing finishes.`}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 motion-safe:animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            Updating live
          </p>
        )}
      </div>
    </div>
  )
}

function Strong({ children }) {
  return <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">{children}</span>
}

// Where the email is: which step it's at, and whether that step is still running.
const STEP_AT = { pending: 1, processing: 2, processed: 3, failed: 3 }

function ProgressTrack({ email }) {
  const at = STEP_AT[email.status] ?? 0
  const failed = email.status === 'failed'
  const finished = email.status === 'processed' || failed
  const finishedAt = finished && email.updated_at ? email.updated_at : null

  const steps = [
    { label: 'Received', icon: Mail, time: email.received_at },
    { label: 'Queued', icon: Clock, time: email.created_at },
    { label: 'Processing', icon: Loader2, spin: true },
    failed
      ? { label: 'Failed', icon: X, time: finishedAt, outcome: true }
      : { label: 'Processed', icon: Check, time: finishedAt, outcome: true },
  ]

  const stateOf = (i) => {
    if (i < at) return 'done'
    if (i > at) return 'upcoming'
    if (failed) return 'failed'
    return finished ? 'done' : 'current'
  }

  return (
    <div>
      <h2 className="sr-only">Progress</h2>
      <ol className="grid grid-cols-4" aria-label="Processing progress">
        {steps.map((step, i) => {
          const state = stateOf(i)
          const Icon = step.icon
          const lineFilled = i < at
          return (
            <li key={step.label} className="relative flex flex-col items-center text-center" aria-current={state === 'current' ? 'step' : undefined}>
              {i < steps.length - 1 && (
                <span aria-hidden="true" className="absolute left-[calc(50%+1.125rem)] right-[calc(-50%+1.125rem)] top-[0.875rem] h-px bg-gray-200 dark:bg-gray-700/80">
                  <span
                    className={`block h-full ${lineFilled ? 'animate-grow-x' : 'scale-x-0'} ${failed && i === steps.length - 2 ? 'bg-rose-300 dark:bg-rose-400/60' : 'bg-navy-400 dark:bg-navy-400/70'}`}
                    style={{ animationDelay: `${200 + i * 180}ms` }}
                  />
                </span>
              )}
              <span
                className={`relative z-[1] flex h-7 w-7 items-center justify-center rounded-full transition-all duration-500 ${
                  state === 'done' && step.outcome
                    ? 'bg-emerald-500 text-white shadow-[0_0_0_4px_rgb(16_185_129/0.14)] animate-copied dark:bg-emerald-500'
                    : state === 'failed'
                      ? 'bg-rose-500 text-white shadow-[0_0_0_4px_rgb(244_63_94/0.14)] animate-copied'
                      : state === 'done'
                        ? 'bg-white text-navy-600 ring-1 ring-navy-200 dark:bg-gray-800 dark:text-navy-300 dark:ring-gray-600'
                        : state === 'current'
                          ? getStatusMeta(email.status).chip
                          : 'border border-dashed border-gray-300 bg-white text-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-600'
                }`}
                style={step.outcome && state !== 'upcoming' ? { animationDelay: `${200 + (steps.length - 1) * 180}ms` } : undefined}
              >
                {state === 'current' && (
                  <span className={`absolute inset-0 rounded-full opacity-30 motion-safe:animate-ping ${getStatusMeta(email.status).bar}`} />
                )}
                {state === 'done' && !step.outcome
                  ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  : <Icon className={`h-3.5 w-3.5 ${state === 'current' && step.spin ? 'motion-safe:animate-spin' : ''}`} strokeWidth={step.outcome ? 2.75 : 2} />}
              </span>
              <span className={`mt-2 text-xs font-medium sm:text-[13px] ${
                state === 'upcoming'
                  ? 'text-gray-400 dark:text-gray-500'
                  : state === 'failed'
                    ? 'text-rose-600 dark:text-rose-300'
                    : step.outcome
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-gray-700 dark:text-gray-300'
              }`}>
                {step.label}
              </span>
              <span className="mt-0.5 hidden min-h-[1rem] text-[11px] tabular-nums text-gray-400 dark:text-gray-500 sm:block">
                {step.time && state !== 'upcoming' ? formatDateTime(step.time) : state === 'current' ? 'In progress' : ''}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

// ── Message ──────────────────────────────────────────────────────────────────

function SectionCard({ icon: Icon, title, count, action, delay, children, sectionRef, className = '' }) {
  return (
    <section
      ref={sectionRef}
      className={`scroll-mt-24 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] animate-rise-in dark:border-gray-800 dark:bg-gray-900 ${className}`}
      style={{ '--rise-delay': delay }}
    >
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800 sm:px-5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy-600 ring-1 ring-inset ring-navy-200/70 dark:bg-navy-500/10 dark:text-navy-300 dark:ring-navy-400/20">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          {count != null && (
            <span key={count} className="rounded-full bg-gray-100 px-1.5 py-px text-[11px] font-semibold tabular-nums text-gray-600 animate-panel-in dark:bg-gray-800 dark:text-gray-300">
              {count}
            </span>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

function splitRecipients(raw) {
  return (raw || '').split(/[;,](?![^<]*>)/).map(r => r.trim()).filter(Boolean)
}

const RECIPIENTS_SHOWN = 3

function MessageCard({ email }) {
  const sender = parseSender(email.sender)
  const recipients = splitRecipients(email.recipients)
  const [allRecipients, setAllRecipients] = useState(false)
  const shown = allRecipients ? recipients : recipients.slice(0, RECIPIENTS_SHOWN)
  const hidden = recipients.length - shown.length

  return (
    <SectionCard
      icon={Mail}
      title="Email content"
      delay="90ms"
      action={email.body ? <CopyButton value={email.body} label="Copy message text" copiedLabel="Message copied" /> : null}
    >
      {/* Envelope */}
      <div className="flex gap-3.5 px-4 py-4 sm:px-5">
        <span
          aria-hidden="true"
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${avatarTone(sender.address || sender.name)}`}
        >
          {initialsFor(sender)}
        </span>
        <dl className="min-w-0 flex-1 space-y-1.5 text-sm">
          <div className="flex flex-col gap-x-3 sm:flex-row sm:items-baseline">
            <dt className="w-16 flex-shrink-0 text-xs font-medium text-gray-400 dark:text-gray-500">From</dt>
            <dd className="min-w-0 [overflow-wrap:anywhere]">
              <span className="font-semibold text-gray-900 dark:text-gray-100">{sender.name}</span>
              {sender.address && sender.address !== sender.name && (
                <span className="text-gray-500 dark:text-gray-400"> &lt;{sender.address}&gt;</span>
              )}
            </dd>
          </div>
          {recipients.length > 0 && (
            <div className="flex flex-col gap-x-3 sm:flex-row sm:items-baseline">
              <dt className="w-16 flex-shrink-0 text-xs font-medium text-gray-400 dark:text-gray-500">To</dt>
              <dd className="flex min-w-0 flex-wrap items-center gap-1.5">
                {shown.map((recipient, i) => {
                  const r = parseSender(recipient)
                  return (
                    <span
                      key={`${recipient}-${i}`}
                      title={recipient}
                      className="inline-flex max-w-full items-center gap-1 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700 animate-panel-in dark:bg-gray-800 dark:text-gray-300"
                    >
                      <AtSign className="h-3 w-3 flex-shrink-0 text-gray-400" aria-hidden="true" />
                      <span className="truncate">{r.name}</span>
                    </span>
                  )
                })}
                {recipients.length > RECIPIENTS_SHOWN && (
                  <button
                    type="button"
                    onClick={() => setAllRecipients(v => !v)}
                    className="rounded-md px-1.5 py-0.5 text-xs font-medium text-navy-600 transition-colors hover:bg-navy-50 dark:text-navy-300 dark:hover:bg-navy-500/10"
                  >
                    {hidden > 0 ? `+${hidden} more` : 'Show fewer'}
                  </button>
                )}
              </dd>
            </div>
          )}
          {email.is_shared_mailbox && email.mailbox_email && (
            <div className="flex flex-col gap-x-3 sm:flex-row sm:items-baseline">
              <dt className="w-16 flex-shrink-0 text-xs font-medium text-gray-400 dark:text-gray-500">Mailbox</dt>
              <dd className="min-w-0">
                <span
                  className="inline-flex max-w-full items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 text-xs font-medium text-indigo-600 ring-1 ring-inset ring-indigo-500/15 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-400/20"
                  title={`Shared mailbox: ${email.mailbox_email}`}
                >
                  <Inbox className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">Shared mailbox · {email.mailbox_email}</span>
                </span>
              </dd>
            </div>
          )}
          {email.received_at && (
            <div className="flex flex-col gap-x-3 sm:flex-row sm:items-baseline">
              <dt className="w-16 flex-shrink-0 text-xs font-medium text-gray-400 dark:text-gray-500">Received</dt>
              <dd className="text-gray-700 dark:text-gray-300">
                <time dateTime={email.received_at}>{formatDateTime(email.received_at)}</time>
                <span className="text-gray-400 dark:text-gray-500"> · {formatRecent(email.received_at)}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Body */}
      <div className="px-4 pb-5 sm:px-5">
        <h3 className="sr-only">Body</h3>
        {email.body
          ? <MessageBody key={email.id} body={email.body} />
          : (
            <div className="flex items-center gap-3 rounded-xl border border-dashed border-gray-200 px-4 py-5 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
              No body content
            </div>
          )}
      </div>
    </SectionCard>
  )
}

// Longer messages fold to this height until expanded.
const BODY_COLLAPSED_PX = 320

function MessageBody({ body }) {
  const textRef = useRef(null)
  const [fullHeight, setFullHeight] = useState(0)
  const [expanded, setExpanded] = useState(false)

  useLayoutEffect(() => {
    const el = textRef.current
    if (!el) return undefined
    const measure = () => setFullHeight(el.scrollHeight || 0)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [body])

  const folds = fullHeight > BODY_COLLAPSED_PX + 48

  return (
    <div className="rounded-xl bg-gray-50/80 ring-1 ring-inset ring-gray-100 dark:bg-gray-800/40 dark:ring-gray-800">
      <div
        className="relative overflow-hidden transition-[max-height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={folds ? { maxHeight: expanded ? fullHeight : BODY_COLLAPSED_PX } : undefined}
      >
        <div ref={textRef} className="whitespace-pre-wrap break-words p-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300 sm:p-5">
          {body}
        </div>
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 bottom-0 h-24 rounded-b-xl bg-gradient-to-t from-gray-50 to-transparent transition-opacity duration-300 dark:from-gray-900 ${folds && !expanded ? 'opacity-100' : 'opacity-0'}`}
        />
      </div>
      {folds && (
        <div className="flex justify-center border-t border-gray-100 px-4 py-2 dark:border-gray-800">
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-navy-700 transition-colors hover:bg-white dark:text-navy-300 dark:hover:bg-gray-800"
          >
            {expanded ? 'Show less' : 'Show the full message'}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}
    </div>
  )
}

// ── Attachments ──────────────────────────────────────────────────────────────

function AttachmentsCard({ documents }) {
  return (
    <SectionCard icon={Paperclip} title="Attachments" count={documents.length} delay="140ms">
      {documents.length === 0 ? (
        <div className="flex flex-col items-center px-4 py-8 text-center animate-panel-in">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800">
            <Paperclip className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </span>
          <p className="mt-2.5 text-sm font-medium text-gray-900 dark:text-gray-100">No attachments</p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">This email arrived without any files.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5">
          {documents.map((doc, index) => (
            <li key={doc.id} className="min-w-0 animate-rise-in" style={{ '--rise-delay': `${200 + Math.min(index, 8) * 50}ms` }}>
              <AttachmentTile doc={doc} />
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}

function AttachmentTile({ doc }) {
  const file = describeFile(doc.filename, doc.content_type)
  return (
    <Link
      to={`/documents/${doc.id}`}
      onMouseMove={trackSpotlight}
      style={{ '--card-glow': file.rgb }}
      className="collection-card group relative flex items-center gap-3.5 rounded-xl border border-gray-200 bg-white p-3.5 transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-gray-300 hover:shadow-[0_10px_24px_-14px_rgba(16,24,40,0.35)] motion-safe:hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/50 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
    >
      <FileGlyph file={file} className="h-14 w-11" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-gray-900 transition-colors group-hover:text-navy-700 dark:text-gray-100 dark:group-hover:text-white" title={doc.filename}>
          {doc.filename}
        </span>
        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
          {file.label} · <span className="tabular-nums">{formatFileSize(doc.file_size_bytes)}</span>
        </span>
        <span className="mt-1.5 flex min-w-0 items-center gap-2">
          {doc.status && <DocumentStatusChip status={doc.status} />}
          <span className="truncate font-mono text-[11px] text-gray-400 dark:text-gray-500" title={doc.content_type || 'Unknown'}>
            {doc.content_type || 'Unknown'}
          </span>
        </span>
      </span>
      <span className="inline-flex flex-shrink-0 items-center gap-1 self-center rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-all duration-300 group-hover:border-navy-200 group-hover:text-navy-700 dark:border-gray-700 dark:text-gray-300 dark:group-hover:border-navy-500/40 dark:group-hover:text-navy-200">
        View
        <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
      </span>
    </Link>
  )
}

// ── Side panel ───────────────────────────────────────────────────────────────

function DetailsCard({ email }) {
  const updated = email.updated_at && email.updated_at !== email.created_at
  return (
    <PanelCard title="Details" delay="140ms">
      <dl className="pb-2">
        <DetailRow icon={CheckCircle2} label="Status">
          <EmailStatusBadge status={email.status} />
        </DetailRow>
        <DetailRow icon={Hash} label="Email ID">
          <span className="flex items-center gap-2">
            <span className="font-mono text-[13px] font-medium">{email.id}</span>
            <CopyButton value={String(email.id)} label="Copy email ID" copiedLabel="ID copied" className="h-6 w-6 !rounded-md" iconClassName="h-3 w-3" />
          </span>
        </DetailRow>
        {email.message_id && (
          <DetailRow icon={AtSign} label="Message ID">
            <span className="flex items-center gap-2">
              <span className="truncate font-mono text-xs" title={email.message_id}>{email.message_id}</span>
              <CopyButton value={email.message_id} label="Copy message ID" copiedLabel="Message ID copied" className="h-6 w-6 !rounded-md" iconClassName="h-3 w-3" />
            </span>
          </DetailRow>
        )}
        {email.received_at && (
          <DetailRow icon={Mail} label="Received">
            <span className="font-medium">{formatDateTime(email.received_at)}</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">{formatRecent(email.received_at)}</span>
          </DetailRow>
        )}
        <DetailRow icon={Calendar} label="Ingested">
          <span className="font-medium">{formatDateTime(email.created_at)}</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">{formatRecent(email.created_at)}</span>
        </DetailRow>
        {updated && (
          <DetailRow icon={History} label="Updated">
            <span className="font-medium">{formatDateTime(email.updated_at)}</span>
          </DetailRow>
        )}
      </dl>
    </PanelCard>
  )
}

function BodyAnalysisCard({ analysis }) {
  const text = JSON.stringify(analysis, null, 2)
  return (
    <PanelCard
      title={<span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" aria-hidden="true" />Body analysis</span>}
      action={<CopyButton value={text} label="Copy body analysis" copiedLabel="Analysis copied" />}
      delay="190ms"
    >
      <div className="p-3 pt-1">
        <pre className="max-h-80 overflow-auto rounded-xl bg-gray-50 p-3 font-mono text-xs leading-5 text-gray-700 ring-1 ring-inset ring-gray-100 dark:bg-gray-800/60 dark:text-gray-300 dark:ring-gray-800">
          {text}
        </pre>
      </div>
    </PanelCard>
  )
}

// ── Requests ─────────────────────────────────────────────────────────────────

// Request | Issuer | Processed | Status | AI confidence | Created by | action
const REQ_GRID = 'lg:grid lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)_7.5rem_11.5rem_8rem_minmax(0,9rem)_2.25rem] lg:items-center lg:gap-4'

function RequestsSection({ sectionRef, requests, loading, loaded, failed, onRefresh }) {
  const refetching = loading && loaded
  return (
    <SectionCard
      sectionRef={sectionRef}
      icon={FileStack}
      title="Requests"
      count={loaded && !failed ? requests.length : null}
      delay="190ms"
      action={
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh requests"
          title="Refresh"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-all duration-200 hover:bg-gray-50 hover:text-gray-800 enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      }
    >
      <div className="relative h-0.5 overflow-hidden" aria-hidden="true">
        {refetching && <span className="collection-progress-bar absolute inset-y-0 left-0 w-1/3 rounded-full bg-navy-500/80 dark:bg-navy-400/80" />}
      </div>

      {failed && (
        <div role="alert" className="mx-4 mb-3 mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 animate-panel-in dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300 sm:mx-5">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">Requests could not be loaded.</span>
          <button type="button" onClick={onRefresh} className="font-semibold hover:underline">Retry</button>
        </div>
      )}

      <div aria-busy={loading} className={`transition-opacity duration-200 ${refetching ? 'opacity-60' : 'opacity-100'}`}>
        {!loaded ? (
          <RequestsSkeleton />
        ) : requests.length === 0 ? (
          !failed && (
            <div className="flex flex-col items-center px-4 py-12 text-center animate-panel-in">
              <div className="relative h-16 w-16">
                <div className="absolute inset-0 rotate-6 rounded-2xl bg-navy-100/60 dark:bg-navy-500/10" />
                <div className="absolute inset-0 -rotate-3 rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <FileStack className="h-7 w-7 text-navy-500 dark:text-navy-300" />
                </div>
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">No requests found</p>
              <p className="mt-0.5 max-w-xs text-sm text-gray-500 dark:text-gray-400">No requests have been created from this email yet.</p>
            </div>
          )
        ) : (
          <>
            <div className={`hidden px-4 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 sm:px-5 ${REQ_GRID}`}>
              <span className="pl-[3.25rem]">Request</span>
              <span>Issuer</span>
              <span>Processed</span>
              <span>Status</span>
              <span>AI confidence</span>
              <span>Created by</span>
              <span className="sr-only">Actions</span>
            </div>
            <ul className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-gray-800/80 dark:border-gray-800">
              {requests.map((req, index) => (
                <RequestRow key={req.id} request={req} index={index} />
              ))}
            </ul>
          </>
        )}
      </div>
    </SectionCard>
  )
}

const MAX_DESC_LENGTH = 50

function RequestRow({ request, index }) {
  const { user } = useAuth()
  const isReader = isReadOnlyUser(user)
  const [label, tone] = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending
  const confidence = request.ai_confidence != null ? Math.round(Math.min(100, Math.max(0, request.ai_confidence))) : 0
  const extracting = request.status === 'processing'
  const barClass = confidence >= 90 ? 'bg-emerald-500' : confidence >= 70 ? 'bg-amber-500' : 'bg-rose-500'
  const createdByName = request.created_by_name || 'Unknown'
  const issuer = request.issuer || request.email?.sender || request.email_sender || 'Unknown Issuer'
  const documents = request.documents || []
  const documentCount = documents.length || request.document_count || 0
  const documentNames = documents.length > 0
    ? documents.map(d => d.filename || d.name || 'Unknown').join('\n')
    : (request.document_names || []).join('\n')
  const description = request.description || ''
  const truncatedDesc = description.length > MAX_DESC_LENGTH ? `${description.substring(0, MAX_DESC_LENGTH)}…` : description
  const title = request.title || request.email_subject || `Request #${String(request.id).substring(0, 8)}`
  const viewOnly = request.status === 'completed' || request.status === 'cancelled'
  const ActionIcon = viewOnly ? Eye : Edit3

  return (
    <li className="group relative animate-rise-in" style={{ '--rise-delay': `${240 + Math.min(index, 10) * 40}ms` }}>
      <span aria-hidden="true" className={`pointer-events-none absolute bottom-2 left-0 top-2 w-[3px] origin-center scale-y-0 rounded-r-full transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-y-100 ${TONE_DOT[tone]}`} />
      <div className={`relative flex flex-col gap-2.5 px-4 py-3.5 transition-colors duration-200 group-hover:bg-gray-50/80 has-[a:focus-visible]:bg-navy-50/60 dark:group-hover:bg-gray-800/40 dark:has-[a:focus-visible]:bg-navy-500/10 sm:px-5 ${REQ_GRID}`}>
        {/* Request — the title link stretches over the whole row. */}
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={`relative flex h-10 w-10 flex-shrink-0 cursor-default items-center justify-center rounded-lg ring-1 ring-inset transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-105 ${TONE_CHIP[tone]}`}
            title={documentCount > 0 ? documentNames : 'No documents'}
          >
            <FileText className="h-5 w-5" aria-hidden="true" />
            <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-navy-600 px-1 text-[10px] font-semibold tabular-nums text-white ring-2 ring-white dark:ring-gray-900">
              {documentCount}
            </span>
          </span>
          <div className="min-w-0 flex-1">
            <Link
              to={`/requests/${request.id}`}
              className="block truncate text-sm font-semibold text-gray-900 transition-colors after:absolute after:inset-0 after:content-[''] focus:outline-none group-hover:text-navy-700 dark:text-gray-100 dark:group-hover:text-white"
            >
              {title}
            </Link>
            {description && (
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400" title={description.length > MAX_DESC_LENGTH ? description : undefined}>
                {truncatedDesc}
              </p>
            )}
            {request.template_name && (
              <p className="mt-0.5 truncate text-[11px] text-gray-400 dark:text-gray-500" title={`Template: ${request.template_name}`}>
                {request.template_name}
              </p>
            )}
          </div>
        </div>

        {/* Issuer */}
        <div className="flex min-w-0 items-center gap-1.5 pl-[3.25rem] text-sm text-gray-600 dark:text-gray-400 lg:pl-0">
          <span className="text-xs text-gray-400 dark:text-gray-500 lg:hidden">Issuer</span>
          <span className="truncate" title={issuer}>{issuer}</span>
          <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 lg:hidden">· by {createdByName}</span>
        </div>

        {/* Processed */}
        <div className="hidden lg:block">
          <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(request.created_at)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{formatTime(request.created_at)}</p>
        </div>

        {/* Status */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pl-[3.25rem] lg:block lg:pl-0">
          <span className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE_CHIP[tone]}`}>
            <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
              {(request.status === 'pending' || extracting) && <span className={`absolute inset-0 rounded-full opacity-60 motion-safe:animate-ping ${TONE_DOT[tone]}`} />}
              <span className={`relative h-1.5 w-1.5 rounded-full ${TONE_DOT[tone]}`} />
            </span>
            <span className="truncate">{label}</span>
          </span>
          {/* Below lg, the time and confidence sit beside the status. */}
          <span className="text-xs text-gray-500 dark:text-gray-400 lg:hidden">
            {formatDate(request.created_at)}, {formatTime(request.created_at)}
          </span>
          <span className="lg:hidden"><Confidence value={confidence} extracting={extracting} barClass={barClass} index={index} /></span>
        </div>

        {/* AI confidence */}
        <div className="hidden lg:block">
          <Confidence value={confidence} extracting={extracting} barClass={barClass} index={index} />
        </div>

        {/* Created by */}
        <div className="hidden min-w-0 items-center gap-2 lg:flex">
          <UserAvatar user={{ id: request.created_by, username: createdByName }} size="sm" className="!h-7 !w-7 !text-[10px]" />
          <span className="truncate text-sm text-gray-600 dark:text-gray-400">{createdByName}</span>
        </div>

        {/* Action */}
        <div className="absolute right-3 top-3 lg:static lg:flex lg:justify-end">
          <Link
            to={`/requests/${request.id}${viewOnly || isReader ? '?mode=view' : '?mode=edit'}`}
            title={viewOnly ? 'View request' : 'Edit request'}
            aria-label={`${viewOnly ? 'View' : 'Edit'} ${title}`}
            className="relative z-[1] inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-all duration-200 hover:bg-white hover:text-navy-700 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/50 dark:hover:bg-gray-800 dark:hover:text-navy-200"
          >
            <ActionIcon className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </li>
  )
}

function Confidence({ value, extracting, barClass, index }) {
  if (extracting) {
    return <span className="text-xs font-medium text-sky-600 dark:text-sky-300">Extracting…</span>
  }
  if (!value) {
    return <span className="text-xs text-gray-400 dark:text-gray-500" title="No confidence score yet">—</span>
  }
  return (
    <span className="inline-flex items-center gap-2" title={`AI confidence ${value}%`}>
      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <span
          className={`block h-full origin-left rounded-full animate-bar-grow ${barClass}`}
          style={{ width: `${value}%`, '--rise-delay': `${240 + Math.min(index, 10) * 40}ms` }}
        />
      </span>
      <span className="text-xs font-semibold tabular-nums text-gray-600 dark:text-gray-300">{value}%</span>
    </span>
  )
}

function RequestsSkeleton() {
  return (
    <ul aria-hidden="true" className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-gray-800/80 dark:border-gray-800">
      {[0, 1].map(i => (
        <li key={i} className={`flex items-center gap-3 px-4 py-3.5 sm:px-5 ${REQ_GRID}`}>
          <div className="flex flex-1 items-center gap-3">
            <div className="skeleton h-10 w-10 rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="skeleton h-4 w-48 max-w-full" />
              <div className="skeleton h-3 w-32" />
            </div>
          </div>
          <div className="skeleton hidden h-4 w-24 lg:block" />
          <div className="skeleton hidden h-8 w-20 lg:block" />
          <div className="skeleton h-5 w-28 rounded-full" />
          <div className="skeleton hidden h-2 w-20 lg:block" />
          <div className="skeleton hidden h-7 w-24 rounded-full lg:block" />
          <div className="skeleton hidden h-7 w-7 rounded-lg lg:block" />
        </li>
      ))}
    </ul>
  )
}

// ── Loading ──────────────────────────────────────────────────────────────────

// Mirrors the loaded layout so nothing jumps when the email arrives.
function DetailSkeleton() {
  return (
    <div className="space-y-5" aria-busy="true" aria-label="Loading email">
      <div className="skeleton h-8 w-20 rounded-lg" />
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center gap-5 p-6">
          <div className="skeleton hidden h-14 w-14 rounded-2xl sm:block" />
          <div className="flex-1 space-y-3">
            <div className="skeleton h-6 w-2/3 max-w-md" />
            <div className="skeleton h-4 w-1/2 max-w-xs" />
          </div>
          <div className="hidden gap-2 lg:flex">
            <div className="skeleton h-9 w-9 rounded-lg" />
            <div className="skeleton h-9 w-24 rounded-lg" />
            <div className="skeleton h-9 w-32 rounded-lg" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 border-t border-gray-100 px-6 py-5 dark:border-gray-800">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="skeleton h-8 w-8 rounded-full" />
              <div className="skeleton h-3 w-16" />
            </div>
          ))}
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_21rem] xl:grid-cols-[minmax(0,1fr)_23rem]">
        <div className="space-y-5">
          <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex gap-3.5">
              <div className="skeleton h-11 w-11 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-64 max-w-full" />
                <div className="skeleton h-4 w-48" />
                <div className="skeleton h-4 w-40" />
              </div>
            </div>
            <div className="skeleton h-40 w-full rounded-xl" />
          </div>
          <div className="grid gap-3 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900 sm:grid-cols-2">
            {[0, 1].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}
          </div>
        </div>
        <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <div className="skeleton h-3 w-20" />
          {[0, 1, 2, 3, 4].map(r => <div key={r} className="skeleton h-4 w-full" />)}
        </div>
      </div>
    </div>
  )
}

export default EmailDetail
